import { useContext, useEffect, useState } from "react"
import { BEGIN_TIME, DEFAULT_AVAILABLE_POINTS, DEFAULT_DATA } from "../../constants/constants"
import { computeScore, computeValues } from "../../constants/formulas"
import { GameContext } from "../../contexts/GameContext"
import { GameState } from "../../interface/GameState"
import GameResults from "./End"
import "./Game.css"
import GameInterface from "./Interface"
import { TimeInterface, displayTime, getMinutesInBetween, stringToTimeInterface } from "../../interface/Time"
import RoundResults from "./Results"
import Scoreboard from "./Scoreboard"
import Instructions from "./Instructions"
import { AppContext } from "../../contexts/AppContext"
import { EquationsService } from "../../services/equations/equationService"
import { toast } from "react-toastify"
import { parse } from "csv-parse/browser/esm/sync";
import { Data } from "../../interface/Data"
import { computeFinalScore } from "../../interface/Util"
import { ResultsService } from "../../services/results/ResultsService"
import { Task, TaskStatus } from "../../interface/Task"
import Reward from "./Reward"
import Thankyou from "./Thankyou"


const DAY_COLUMN = 0;
const N_PEOPLE_COLUMN = 4;
const TEMP_COLUMN = 8;
const WEATHER_COLUMN = 9;

interface Props {
  prop: string
}


function getColumn(arr: number[][], n: number) {
  const columnValues: number[][] = [[]];
  arr.forEach((x, row) => {
    // Skip column names.
    if (row > 0 && x[DAY_COLUMN].toString() !== '') {
      if (+x[DAY_COLUMN] >= columnValues.length) { columnValues.push([]); }
      const value = +x[n];
      columnValues[x[DAY_COLUMN]].push(value);
    }
  });
  return columnValues;
}

const Game = ({ prop }: Props) => {
  const { gameState, setGameState, setBounds, round, setRound, data, setData, results, setResults, maxPoints, setDisableAdvanceRound,
    setValues, setPoints, setScores, setFinalScores, setTasks, setEvents, setScales, setNPeople, finalScores, stepDuration, setTime,
    setSpontaneousEvents, scores, setWeatherIdx, setTempExt, setBeginTime, beginTime, endTime, setCurrentStep, setMaxPoints, tasks,
    setEndTime, setMaxRound, setStepDuration, maxRound, setInterfaceParams, defaultValues, setDefaultValues ,startAt} = useContext(GameContext);

  const { jwtToken, socket } = useContext(AppContext);

  const [defaultTasks, setDefaultTasks] = useState<Task[]>([]);

  const [resultId, setResultId] = useState<string|null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (jwtToken) {
        // Fetch files.
        const configResponse = await EquationsService.getGameConfigFile(jwtToken);
        const defaultResponse = await EquationsService.getGameDefaultValuesFile(jwtToken);
        const paramsResponse = await EquationsService.getParametersFile(jwtToken);

        if (defaultResponse.status === 200) {
          setInterfaceParams(defaultResponse.data);
        }

        if (configResponse.status === 200 && paramsResponse.status === 200) {

          const gameConfig = configResponse.data;
          // Set Config variables.
          setMaxRound(gameConfig.nRounds);
          setStepDuration(gameConfig.step);

          const begin: TimeInterface[] = [];
          const end: TimeInterface[] = [];
          gameConfig.durations.forEach((d: { begin: string; end: string }) => {
            begin.push(stringToTimeInterface(d.begin));
            end.push(stringToTimeInterface(d.end));
          });

          setBeginTime(begin);
          setEndTime(end);

          setDefaultValues(gameConfig.default);

          const defaultPoints = [];
          for (let i = 0; i < gameConfig.default.length; ++i) {
            defaultPoints.push(gameConfig.default[i]["energy"] / gameConfig.scales["energy"]);
          }

          setMaxPoints(defaultPoints);
          setPoints(defaultPoints[0]);
          console.log(defaultPoints)

          setData(gameConfig.default[round]);
          setBounds(gameConfig.bounds);
          setScales(gameConfig.scales);

          setTasks(gameConfig.dailyTasks);
          setDefaultTasks(gameConfig.dailyTasks);
          setEvents(gameConfig.events);
          // setSpontaneousEvents(gameConfig.untimelyEvents);

          // Set Parameter variables      
          const params = parse(paramsResponse.data);  // <-- 2D array of the csv file.
          const temps = getColumn(params, TEMP_COLUMN);
          const weathers = getColumn(params, WEATHER_COLUMN);

          const people = getColumn(params, N_PEOPLE_COLUMN);
          setTempExt(temps);
          setWeatherIdx(weathers);
          setNPeople(people);
        } else {
          toast("Erreur de communication avec le serveur.");
        }
      }
    }

    fetchData();
  }, [prop]);

  const onEndRound = () => {

    setGameState(GameState.ROUND_RESULT);
    
    
    const dataList = [];
    const currentDaySteps = getMinutesInBetween({ hours: 24, minutes: 0 }, endTime[round]) / stepDuration;
    const nextDaySteps = getMinutesInBetween(beginTime[round + 1], { hours: 0, minutes: 0 }) / stepDuration;
    const nSteps = currentDaySteps + nextDaySteps;

    for (let i = 0; i < nSteps; ++i) { dataList.push({ data: { "electric_task": 0, ...data } }); }

    console.log("data: ",dataList)
    socket?.emitData(dataList);
    setDisableAdvanceRound(true);
    
    setTasks(defaultTasks.slice());
  }


  const onEndGame = (savedScores: number[]) => {
    const obj = {
      "score": computeFinalScore(savedScores),
      "results": results,
      "start_at": startAt.getTime()
    }

    if (jwtToken) {
      ResultsService.sendResults(jwtToken, obj).then((res) => {
        console.log(res);
        setResultId(res.data.id)
      });
    }
  }


  const onNextRound = (newRoundScore: number) => {
    const newRound = round + 1;
    setCurrentStep(0);

    const finalScoresClone = finalScores.slice();
    finalScoresClone.push(newRoundScore);
    setFinalScores(finalScoresClone);

    const resultsClone = results.slice();
    resultsClone[round]["score"] = newRoundScore;
    setResults(resultsClone);

    console.log("ROUND : ->",newRound, maxRound)
    if (newRound >= maxRound) {
      setGameState(GameState.GAME_END);
      onEndGame(finalScoresClone)
      socket?.disconnect();
      return;
    }

    const scoresClone = scores.slice();
    const lastScore = scoresClone[round][scoresClone[round].length - 1];
    scoresClone.push([lastScore]);
    setScores(scoresClone);

    resultsClone.push({ "timestamps": [], "score": 0 });
    setResults(resultsClone);

    setPoints(maxPoints[newRound]);
    setTime(beginTime[newRound]);

    // Reset tasks status.
    const clonedTasks = tasks.slice();
    clonedTasks.forEach((t) => {
      t.status = TaskStatus.AVAILABLE;
    });

    setRound(newRound);
    setGameState(GameState.ROUND);
  }

  useEffect(() => {
    console.log(results)
  }, [results])

  return (
    <div style={{ "display": "flex", "flexDirection": "column", "margin": "auto" }}>
      <div className="game">

        {gameState == GameState.INSTRUCTIONS && <Instructions />}

        {gameState == GameState.ROUND && <GameInterface onEndRound={onEndRound} />}

        {gameState == GameState.ROUND_RESULT && <RoundResults roundScores={scores[round]} nextRoundFn={onNextRound} />}

        {gameState == GameState.GAME_END && <GameResults nextPageFn={() => setGameState(GameState.SCOREBOARD)} />}

        {gameState == GameState.SCOREBOARD && <Scoreboard />}

        {gameState == GameState.REWARD && <Reward resultId={resultId} />}

        {gameState == GameState.THANK_YOU && <Thankyou />}

      </div>
    </div>
  );
}

export default Game;
